<script>
import utils from '@/common/utils';
import { defineComponent, ref, computed, watch, inject} from 'vue'
import { useQuestionKeyboardNav } from '@/composables/useQuestionKeyboardNav'
import { buildDraftInputs, selectEvidence, draftEvidenceStatus, draftDisabledReason, draftGateHint } from '@rm-ui/levelling/rationaleDraft'
import RationaleEditor from './RationaleEditor.vue'

export default defineComponent({
  name: 'ClassificationQuestion',
  components: { RationaleEditor },
  props: {
    factor: {
      type: Object,
      required: true
    },
    levels: {
      type: Array,
      required: true
    },
    allLevels: {
      type: Array,
      default: () => []
    },
    currentIndex: {
      type: Number,
      required: true
    },
    totalFactors: {
      type: Number,
      required: true
    },
    previousAnswer: {
      type: Object,
      default: null
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    /* CEO ceiling (PD-1258): why the top of the ladder is missing. Empty unless the
       ceiling actually removed levels from this track. */
    ceilingNote: {
      type: String,
      default: null
    },
    canDelete: {
      type: Boolean,
      default: false
    },
    editing: {
      type: Boolean,
      default: false
    },
    /* AI rationale drafting (PD-1301) — see SubFactorQuestion. */
    rationaleDraft: {
      type: Object,
      default: null
    },
  },
  emits: ['answer', 'back', 'restart', 'cancel', 'delete'],
  setup(props, { emit }) {
    const loginState = inject('loginState');
    const selectedLevel = ref(props.previousAnswer?.selectedLevel ?? null)
    const comments = ref(props.previousAnswer?.comments ?? null)
    const rationaleProvenance = ref(props.previousAnswer?.rationaleProvenance ?? null)
    const showAllLevels = ref(false)
    const updatedBy = computed(() => props.previousAnswer?.updated_by ?? loginState.user.name)
    const updatedDate = computed(() => props.previousAnswer && props.previousAnswer.comments ? props.previousAnswer.updated_date : null)
    const showFactorInfo = ref(false)

    // An empty rationale is stored as null, so normalise before comparing —
    // otherwise opening and closing the editor would look like a change.
    function normaliseComment(value) {
      const text = (value ?? '').trim()
      return text === '' ? null : text
    }

    // True only when the level or the rationale differs from the stored answer.
    // Drives the Update button and lets Next/Previous skip a pointless save.
    const isDirty = computed(() =>
      selectedLevel.value !== (props.previousAnswer?.selectedLevel ?? null) ||
      normaliseComment(comments.value) !== normaliseComment(props.previousAnswer?.comments) ||
      (normaliseComment(comments.value) !== null &&
        (rationaleProvenance.value ?? null) !== (props.previousAnswer?.rationaleProvenance ?? null))
    )

    const factorColor = computed(() => {
      return props.factor && props.factor.backgroundColour ? utils.shadeColor(props.factor.backgroundColour, 20, -20) : null
    })

    watch(() => props.factor.name, () => {
      showAllLevels.value = false
    }, { immediate: true })

    watch(() => props.previousAnswer, (newVal) => {
      selectedLevel.value = newVal?.selectedLevel ?? null
      comments.value = newVal?.comments ?? null
      rationaleProvenance.value = newVal?.rationaleProvenance ?? null
    })

    const zoneLevelNumbers = computed(() => {
      return new Set(props.levels.map(l => l.level))
    })

    const displayedLevels = computed(() => {
      if (showAllLevels.value)
        return props.allLevels;

      if (!selectedLevel.value || props.levels.some(l => l.level === selectedLevel.value) || !props.allLevels.some(l => selectedLevel.value === l.level))
        return props.levels;

      return [...props.levels, props.allLevels.find(l => selectedLevel.value === l.level)]
        .sort((a, b) => a.level - b.level)
    })

    function isZoneLevel(level) {
      return zoneLevelNumbers.value.has(level)
    }

    function selectLevel(level) {
      selectedLevel.value = level
    }

    function submitAnswer() {
      if (selectedLevel.value !== null) {
        emit('answer', {
          selectedLevel: selectedLevel.value,
          comments: comments.value,
          rationaleProvenance: normaliseComment(comments.value) === null ? null : rationaleProvenance.value,
          dirty: isDirty.value
        })
      }
    }

    const { keyHint } = useQuestionKeyboardNav({
      options: () => displayedLevels.value.map(l => ({ key: Number(l.abbreviation?.match(/\d+/)?.[0] ?? l.level), value: l.level })),
      onSelect: selectLevel,
      onNext: submitAnswer,
      onPrev: () => emit('back'),
      canNext: () => selectedLevel.value !== null,
      canNavigate: () => !props.editing,
      verb: 'select',
    })

    // --- Rationale (PD-1301) ---
    function onRationaleUpdate({ text, provenance }) {
      comments.value = text || null
      rationaleProvenance.value = text ? provenance : null
    }

    const canDraft = computed(() => !!(props.rationaleDraft && props.rationaleDraft.enabled))
    // A classification unit covers the whole factor, so no sub-factor filter.
    const evidenceStatus = computed(() => draftEvidenceStatus(props.rationaleDraft, null))
    const draftDisabled = computed(() => draftDisabledReason({
      levelSelected: selectedLevel.value !== null,
      evidenceStatus: evidenceStatus.value,
    }))
    const draftPending = computed(() => evidenceStatus.value === 'pending')
    const draftHint = computed(() => draftGateHint(evidenceStatus.value))
    watch(evidenceStatus, (s) => {
      if (s === 'pending' && props.rationaleDraft && props.rationaleDraft.ensureEvidence) props.rationaleDraft.ensureEvidence()
    }, { immediate: true })

    // A classification unit covers the whole factor, so the draft sends the
    // sub-factor slots empty and uses the track level (with THIS factor's
    // descriptor) as the selected level. Evidence is not narrowed by sub-factor.
    function buildDraftRequest() {
      const ctx = props.rationaleDraft || {}
      const tl = [...props.levels, ...props.allLevels].find(l => l.level === selectedLevel.value) || null
      // `number` must be an integer for the draft endpoint: the framework level
      // position, never the abbreviation (e.g. "P5").
      const level = tl
        ? { number: Number(tl.levelPosition ?? tl.level), name: tl.name || String(tl.level), description: tl.description || '' }
        : null
      return {
        chainId: ctx.chainId,
        systemReference: ctx.systemReference,
        sourceCustom: { factor_id: props.factor.id, level: selectedLevel.value },
        getEvidence: ctx.getEvidence,
        ensureEvidence: ctx.ensureEvidence,
        buildInputs: (items) => buildDraftInputs({
          factor: props.factor,
          subFactor: null,
          level,
          evidence: selectEvidence(items, { topK: ctx.topK }),
        }),
      }
    }

    return {
      props,
      showFactorInfo,
      factorColor,
      selectedLevel,
      showAllLevels,
      displayedLevels,
      isZoneLevel,
      selectLevel,
      submitAnswer,
      isDirty,
      comments,
      rationaleProvenance,
      updatedBy,
      updatedDate,
      onRationaleUpdate,
      canDraft,
      draftDisabled,
      draftPending,
      draftHint,
      buildDraftRequest,
      keyHint,
    }
  }
})
</script>

<template>
  <div class="lvl-eval">
    <div class="lvl-qcard">
      <div class="lvl-qcard-in">
        <div class="lvl-q-head">
          <!-- Context label -->
          <div class="lvl-q-eyebrow">
            <span v-if="factorColor" class="lvl-fdot" :style="{ background: factorColor }"></span>
            Classification
          </div>

          <!-- Factor name -->
          <h2 class="lvl-q-title">
            {{ factor.name }}
            <span v-if="factor.description" class="lvl-ghead-info">
              <button
                type="button"
                class="lvl-ghead-ibtn"
                :class="{ on: showFactorInfo }"
                :aria-expanded="showFactorInfo"
                :aria-label="'About ' + factor.name"
                title="What this factor covers"
                @click="showFactorInfo = !showFactorInfo"
              >
                <span class="ms" style="font-size:15px">info</span>
              </button>
              <template v-if="showFactorInfo">
                <div class="lvl-help-catch" @click="showFactorInfo = false"></div>
                <div class="lvl-ghead-pop" role="tooltip">
                  <span class="lvl-ghead-pop-title">{{ factor.name }}</span>
                  <p>{{ factor.description }}</p>
                </div>
              </template>
            </span>
          </h2>
        </div>

        <div class="lvl-q-body">
          <!-- Prompt -->
          <!-- CEO ceiling (PD-1258): the per-factor list is filtered by the same
               choke point as the zone step, so it needs the same explanation.
               Without it the top of the ladder is simply absent with no reason. -->
          <div v-if="ceilingNote" class="lvl-note lvl-note--info lvl-anchor-ceilnote">
            <span class="ms">anchor</span>
            <span>{{ ceilingNote }}</span>
          </div>

          <div class="lvl-q-prompt">Select the level that best describes this role:</div>

          <!-- Level options -->
          <div class="lvl-trk-opts" role="radiogroup">
            <button
              v-for="item in displayedLevels"
              :key="item.level"
              type="button"
              role="radio"
              :aria-checked="selectedLevel === item.level"
              class="lvl-trk-opt"
              :class="{
                on: selectedLevel === item.level,
                out: showAllLevels && !isZoneLevel(item.level)
              }"
              @click="selectLevel(item.level)"
              :data-testid="'classification-' + factor.id + '-option-' + item.level"
            >
              <span class="lvl-trk-radio" :class="{ on: selectedLevel === item.level }"></span>
              <span class="lvl-trk-main">
                <span class="lvl-trk-name">
                  <span class="lvl-trk-lvln">{{ item.abbreviation }}</span>
                </span>
                <span v-if="item.description" class="lvl-trk-desc">{{ item.description }}</span>
              </span>
            </button>
          </div>

        </div>

        <!-- Show all levels toggle -->
        <div class="lvl-showall-wrap">
          <button
            type="button"
            class="lvl-showall"
            :class="{ on: showAllLevels }"
            role="switch"
            :aria-checked="showAllLevels"
            @click="showAllLevels = !showAllLevels"
          >
            <span class="lvl-showall-sw"></span>
            <span class="lvl-showall-lbl">Show all levels</span>
          </button>
          <span class="lvl-showall-hint">Reveal levels outside the suggested zone for this role.</span>
        </div>

        <hr class="lvl-q-divider" />

        <!-- Rationale (PD-1301: with "Use Atlas") -->
        <RationaleEditor
          :comment="comments"
          :provenance="rationaleProvenance"
          :author="updatedBy"
          :date="updatedDate"
          :read-only="readOnly"
          :can-draft="canDraft"
          :draft-disabled-reason="draftDisabled"
          :draft-pending="draftPending"
          :draft-hint="draftHint"
          :draft-request="buildDraftRequest"
          @update="onRationaleUpdate"
        />
      </div>

      <!-- Editing a single answer from the summary: only Cancel / Update -->
      <div v-if="editing" class="lvl-cardfoot">
        <span class="restart"></span>
        <span class="prog">Editing one answer · your other answers stay as they are</span>
        <span class="nav">
          <button type="button" class="lvl-btn lvl-btn--text" @click="$emit('cancel')">Cancel</button>
          <button type="button" class="lvl-btn" :disabled="selectedLevel === null || !isDirty" @click="submitAnswer"><span class="ms">check</span> Update</button>
        </span>
      </div>
      <div v-else class="lvl-cardfoot">
        <span class="restart">
          <button type="button" class="lvl-btn lvl-btn--outlined" :disabled="readOnly" @click="$emit('restart')">Restart</button>
          <button v-if="canDelete" type="button" class="lvl-btn lvl-btn--danger" :disabled="readOnly" @click="$emit('delete')">Delete</button>
        </span>
        <span class="prog">factor {{ currentIndex + 1 }} of {{ totalFactors }}<template v-if="keyHint"> · {{ keyHint }}</template></span>
        <span class="nav">
          <button type="button" class="lvl-btn lvl-btn--outlined" @click="$emit('back')">Previous</button>
          <button type="button" class="lvl-btn" :disabled="selectedLevel === null" @click="submitAnswer">Next <span class="ms">arrow_forward</span></button>
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" src="./evaluationTheme.scss"></style>

<style scoped>
.lvl-rationale-time { display: block; margin-top: 6px; padding-left: 2px; font-size: 11px; color: var(--v3-text-3); }
.lvl-fdot {
  display: inline-block;
  width: 22px;
  height: 9px;
  border-radius: 999px;
  flex: 0 0 auto;
}
.eval-docked .lvl-qcard { max-height: calc(100dvh - 158px); }
.eval-docked .lvl-qcard-in { flex: 1 1 auto; min-height: 0; }
.eval-docked .lvl-q-head { flex: 0 0 auto; }
.eval-docked .lvl-q-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-gutter: stable;
  padding-right: 10px;
  padding-bottom: 5px;
}
</style>
